import React from 'react'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Edit, Eye, EyeOff } from 'lucide-react'
import useOpenDrawer from '@/hooks/useOpenDrawer'
import FabricCombinationEditForm from './FabricCombinationEditForm'

interface FabricCombinationOptionProfile {
  id: number
  name: string
  description: string | null
  language: string
  __typename: string
}

interface FabricCombinationImage {
  id: number
  src_md?: string
  src_xs?: string
}

interface FabricCombinationOption {
  id: number
  code: string
  image: FabricCombinationImage | null
  fabricCombinationOptionProfiles: FabricCombinationOptionProfile[]
  __typename: string
}

interface FabricCombinationProfile {
  id: number
  name: string
  description: string | null
  language: string
  __typename: string
}

interface FabricCombination {
  id: number
  code: string
  enabled: boolean
  visible: boolean
  image: FabricCombinationImage | null
  fabricCombinationOptions: FabricCombinationOption[]
  fabricCombinationProfiles: FabricCombinationProfile[]
  __typename: string
}

interface FabricCombinationsCardProps {
  fabricCombinations: FabricCombination[]
  language: string
  advancedProductId: number
}

export function FabricCombinationsCard({
  fabricCombinations,
  language,
  advancedProductId,
}: FabricCombinationsCardProps) {
  const openFabricCombinationEdit = useOpenDrawer(FabricCombinationEditForm)
  if (!fabricCombinations || fabricCombinations.length === 0) {
    return null
  }

  // Sort fabric combinations by code if available, otherwise by id
  const sortedCombinations = [...fabricCombinations].sort((a, b) => {
    if (a.code && b.code) {
      return a.code.localeCompare(b.code)
    }
    return a.id - b.id
  })

  return (
    <Card>
      <CardHeader>
        <CardTitle>Fabric Combinations</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {sortedCombinations.map(combination => {
            const profile =
              combination.fabricCombinationProfiles.find(
                profile =>
                  profile.language.toLowerCase() === language.toLowerCase()
              ) || combination.fabricCombinationProfiles[0]

            return (
              <div
                key={combination.id}
                className={`border rounded-md p-4 ${
                  !combination.enabled ? 'opacity-50' : ''
                }`}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center">
                    <h3 className="text-lg font-medium mr-2">
                      {profile?.name || `Combination ${combination.code}`}
                    </h3>
                    <Badge variant="outline">{combination.code}</Badge>
                    <Badge
                      variant={combination.enabled ? 'green' : 'secondary'}
                      className="ml-2"
                    >
                      {combination.enabled ? 'Active' : 'Inactive'}
                    </Badge>
                    <Badge
                      variant={combination.visible ? 'lightBlue' : 'secondary'}
                      className="ml-2 flex items-center gap-1"
                    >
                      {combination.visible ? (
                        <Eye className="h-3 w-3" />
                      ) : (
                        <EyeOff className="h-3 w-3" />
                      )}
                    </Badge>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      openFabricCombinationEdit({
                        fabricCombination: combination,
                        advancedProductId,
                      })
                    }
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                </div>

                {profile?.description && (
                  <p className="text-sm text-gray-500 mb-3">
                    {profile.description}
                  </p>
                )}

                {combination.image && combination.image.src_md && (
                  <div className="mb-3">
                    <img
                      src={combination.image.src_md}
                      alt={profile?.name || `Combination ${combination.code}`}
                      className="w-full max-h-32 object-contain rounded-md"
                    />
                  </div>
                )}

                {combination.fabricCombinationOptions.length > 0 && (
                  <div className="mt-3">
                    <h4 className="text-sm font-medium mb-2">Zones:</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {/* Sort options by code if available, otherwise by id */}
                      {[...combination.fabricCombinationOptions]
                        .sort((a, b) => {
                          if (a.code && b.code) {
                            return a.code.localeCompare(b.code)
                          }
                          return a.id - b.id
                        })
                        .map(option => {
                          const optionProfile =
                            option.fabricCombinationOptionProfiles.find(
                              profile =>
                                profile.language.toLowerCase() ===
                                language.toLowerCase()
                            ) || option.fabricCombinationOptionProfiles[0]

                          return (
                            <div
                              key={option.id}
                              className="flex items-center space-x-3 border rounded-md p-2"
                            >
                              {option.image && option.image.src_xs && (
                                <img
                                  src={option.image.src_xs}
                                  alt={
                                    optionProfile?.name ||
                                    `Option ${option.code}`
                                  }
                                  className="w-12 h-12 object-cover rounded-md"
                                />
                              )}
                              <div>
                                <div className="flex items-center space-x-2">
                                  <span className="font-medium">
                                    {optionProfile?.name ||
                                      `Option ${option.code}`}
                                  </span>
                                  <Badge variant="outline" className="text-xs">
                                    {option.code}
                                  </Badge>
                                </div>
                                {optionProfile?.description && (
                                  <p className="text-xs text-gray-500">
                                    {optionProfile.description}
                                  </p>
                                )}
                              </div>
                            </div>
                          )
                        })}
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </CardContent>
    </Card>
  )
}
